import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Check, ChevronRight, Play, Sprout, Zap } from 'lucide-react';
import { Skeleton, cn } from '@ai-tutor/ui';
import { useDailyPlan } from '@/hooks/mindspace/useRevision';
import { StreakBadge } from '@/components/features/mindspace/practice/widgets/StreakBadge';
import type { DailyPlanSummary } from '@/types/api';

const PRACTICE = '/mindspace/practice-arena';
const QUICK_BYTES = '/mindspace/quick-bytes';

/** Most concepts drawn as dots; the rest read "+N". */
const MAX_DOTS = 12;

// The app's own card and colours - the theme's surface, its primary button,
// the soft brand tint its icon chips use. `relative isolate`: over the page
// lattice, which paints over any surface that is not positioned.
const CARD =
  'relative isolate rounded-3xl border border-border bg-card p-5 shadow-elevation-1 motion-safe:animate-fade-in-up';

const PRIMARY =
  'flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-primary text-base font-semibold text-primary-foreground shadow-sm transition-transform duration-fast active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card motion-reduce:active:scale-100';

const SECONDARY =
  'flex h-12 w-full items-center justify-center gap-2 rounded-2xl border border-border bg-card text-base font-semibold text-foreground transition-colors duration-fast hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

const CHIP = 'dark:tint-brand bg-brand-100 text-brand-700';

type DayState = 'due' | 'caught-up' | 'new';

/**
 * Which of three days this is. "New" is nothing to practise in ANY pack - no
 * concepts yet, so there is nothing to be caught up on; "caught up" is
 * nothing due today, with concepts to practise anyway.
 */
function dayState(plan: DailyPlanSummary): DayState {
  if (plan.summary.practice.count > 0) return 'due';
  const packs = Object.values(plan.starter_packs ?? {});
  const anything =
    packs.some(p => p.count > 0) || plan.summary.quick_bytes.count > 0;
  return anything ? 'caught-up' : 'new';
}

/** "FRI 16 OCT" - the label register, in mono. */
function todayLabel(): string {
  return new Date()
    .toLocaleDateString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    })
    .replace(',', '')
    .toUpperCase();
}

/**
 * The phone main page's lead: what to do in Mindspace today, from the daily
 * plan Practice Arena reads (so opening it next is instant - same query).
 *
 * One action, with the real numbers: "7 concepts due ... Start review, ~7
 * min". The three-card dashboard it replaces on a phone said none of that -
 * its previews were placeholder animations - so a user had to open Practice
 * Arena to learn whether anything was waiting. The concepts due are also
 * DRAWN, one dot each, popping in as the card lands: the dot is Mindspace's
 * motif (the archive's graph, the dashboard's dot grid).
 *
 * "Start review" opens Practice Arena, where the Due today session is the
 * recommended one. It does not start a session from here: that would be a
 * second way in, and Practice Arena owns the mode choice.
 */
export function MindspaceToday() {
  const { data: plan, isLoading } = useDailyPlan();
  if (isLoading || !plan) return <TodaySkeleton />;

  const state = dayState(plan);
  const bytes = plan.summary.quick_bytes.count;

  return (
    <section aria-label='Today' className={CARD}>
      <div className='flex items-center justify-between gap-3'>
        <h2 className='font-mono text-xs font-medium tracking-[0.14em] text-muted-foreground'>
          <span className='sr-only'>Today, </span>
          <span aria-hidden='true'>TODAY · </span>
          {todayLabel()}
        </h2>
        <StreakBadge />
      </div>

      {state === 'due' && <DueBody plan={plan} />}
      {state === 'caught-up' && (
        <StateBody
          icon={<Check className='h-5 w-5' aria-hidden='true' />}
          title='All caught up'
          body="Nothing is due today. Practise anytime - it won't change your schedule."
          action={
            <Link to={PRACTICE} className={cn(SECONDARY, 'mt-5')}>
              Open Practice Arena
            </Link>
          }
        />
      )}
      {state === 'new' && (
        <StateBody
          icon={<Sprout className='h-5 w-5' aria-hidden='true' />}
          title='Nothing to review yet'
          body='Concepts from your lessons collect here, ready to review. Study a lesson to start.'
          action={
            <Link to='/my-learning' className={cn(PRIMARY, 'mt-5')}>
              Go to My Learning
            </Link>
          }
        />
      )}

      {state !== 'new' && bytes > 0 && (
        <Link
          to={QUICK_BYTES}
          className='-mx-5 -mb-5 mt-5 flex min-h-[3.25rem] items-center gap-3 rounded-b-3xl border-t border-border px-5 text-sm text-muted-foreground transition-colors duration-fast hover:bg-muted focus-visible:bg-muted focus-visible:outline-none'
        >
          <span
            className={cn(
              'flex h-7 w-7 items-center justify-center rounded-full',
              CHIP
            )}
          >
            <Zap className='h-3.5 w-3.5' aria-hidden='true' />
          </span>
          <span className='flex-1'>
            <span className='font-semibold tabular-nums text-foreground'>
              {bytes}
            </span>{' '}
            Quick {bytes === 1 ? 'Byte' : 'Bytes'} for today
          </span>
          <ChevronRight className='h-4 w-4' aria-hidden='true' />
        </Link>
      )}
    </section>
  );
}

function DueBody({ plan }: { plan: DailyPlanSummary }) {
  const { count, estimated_minutes: minutes } = plan.summary.practice;
  const dots = Math.min(count, MAX_DOTS);
  return (
    <>
      <p className='mt-4 flex items-baseline gap-2.5 text-foreground'>
        <span className='font-mono text-6xl font-semibold tabular-nums leading-none'>
          {count}
        </span>
        <span className='text-lg font-medium'>
          {count === 1 ? 'concept' : 'concepts'} due
        </span>
      </p>
      {/* One dot per concept due, popping in left to right. */}
      <div
        aria-hidden='true'
        className='mt-4 flex flex-wrap items-center gap-1.5'
      >
        {Array.from({ length: dots }, (_, i) => (
          <span
            key={i}
            data-due-dot
            className='h-2.5 w-2.5 rounded-full bg-primary motion-safe:animate-in motion-safe:fade-in motion-safe:zoom-in-50 motion-safe:fill-mode-both'
            style={{ animationDelay: `${150 + i * 45}ms` }}
          />
        ))}
        {count > MAX_DOTS && (
          <span className='ml-1 font-mono text-xs text-muted-foreground'>
            +{count - MAX_DOTS}
          </span>
        )}
      </div>
      <Link to={PRACTICE} className={cn(PRIMARY, 'mt-5')}>
        <Play className='h-4 w-4 fill-current' aria-hidden='true' />
        Start review
        {minutes > 0 && <span className='font-normal'>· ~{minutes} min</span>}
      </Link>
    </>
  );
}

function StateBody({
  icon,
  title,
  body,
  action,
}: {
  icon: ReactNode;
  title: string;
  body: string;
  action: ReactNode;
}) {
  return (
    <>
      <div className='mt-4 flex items-center gap-3'>
        <span
          className={cn(
            'flex h-10 w-10 items-center justify-center rounded-full',
            CHIP
          )}
        >
          {icon}
        </span>
        <p className='text-2xl font-semibold text-foreground'>{title}</p>
      </div>
      <p className='mt-2 text-sm leading-relaxed text-muted-foreground'>
        {body}
      </p>
      {action}
    </>
  );
}

/** Same box as the loaded card, so nothing moves when the plan lands. */
function TodaySkeleton() {
  return (
    <section
      aria-busy='true'
      aria-label='Today'
      className={cn(CARD, 'min-h-[15.5rem]')}
    >
      <Skeleton className='h-3 w-32' />
      <Skeleton className='mt-5 h-14 w-48 rounded-xl' />
      <Skeleton className='mt-4 h-2.5 w-36' />
      <Skeleton className='mt-5 h-12 w-full rounded-2xl' />
    </section>
  );
}
